import {
  collection,
  doc,
  getDoc,
  getFirestore,
  serverTimestamp,
  runTransaction,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { buildLatestProgramMirror } from './latestProgramMirror';

function makeUniqueId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function computeNextWorkingDate(startDate, workingDays = 3) {
  const result = new Date(startDate);
  let added = 0;
  while (added < workingDays) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) {
      added += 1;
    }
  }
  return result;
}

function normalizeAlimentazioni(list = []) {
  return list.map((entry) => {
    if (entry && (!entry.id || String(entry.id).trim() === '')) {
      return { ...entry, id: makeUniqueId() };
    }
    return entry;
  });
}

function buildNewAlimentazione(now, extras = {}) {
  const {
    percorsoId = null,
    percorsoTitle = null,
    percorsoSequenceIndex = null,
    percorsoSequenceTotal = null,
    invioStepKey = null,
    invioStepIndex = null,
    invioStepLabel = null,
    invioStepSequenceIndex = null,
    invioStepSequenceTotal = null,
    invioDeliveryTargetMode = null,
  } = extras || {};
  const normalizedDeliveryTargetMode =
    invioDeliveryTargetMode === 'next' || invioDeliveryTargetMode === 'current'
      ? invioDeliveryTargetMode
      : null;
  return {
    dataCreazione: Timestamp.fromDate(now),
    id: makeUniqueId(),
    kcalalimentazioni: '',
    strategia: '',
    linkAlimentazione: '',
    pianoIntegrazione: '',
    allenamento: '',
    freqallenamento: '',
    lavtrainer: '',
    lavorato: '',
    dataFileCaricato: null,
    dataSelezioneSI: null,
    nota1: '',
    nota2: '',
    videocall: '',
    nutrizionista: '',
    nutrizionistadiet: '',
    notetrainer: '',
    noteselect: '',
    noteinterna: '',
    percorsoId: percorsoId || null,
    percorsoTitle: percorsoTitle || null,
    percorsoSequenceIndex: Number.isFinite(percorsoSequenceIndex) ? Number(percorsoSequenceIndex) : null,
    percorsoSequenceTotal: Number.isFinite(percorsoSequenceTotal) ? Number(percorsoSequenceTotal) : null,
    invioStepKey: invioStepKey || null,
    invioStepIndex: Number.isFinite(invioStepIndex) ? Number(invioStepIndex) : null,
    invioStepLabel: (invioStepLabel || '').trim() || null,
    invioStepSequenceIndex: Number.isFinite(invioStepSequenceIndex)
      ? Number(invioStepSequenceIndex)
      : null,
    invioStepSequenceTotal: Number.isFinite(invioStepSequenceTotal)
      ? Number(invioStepSequenceTotal)
      : null,
    invioDeliveryTargetMode: normalizedDeliveryTargetMode,
    consegnaConfirmed: false,
    consegnaConfirmedAt: null,
    progCustomizzato: false,
    progCustomizzatoAt: null,
  };
}

async function bumpUserCycleAfterDailySend(userId) {
  if (!userId) return;
  try {
    const db = getFirestore();
    const userRef = doc(db, 'users', userId);
    const snap = await getDoc(userRef);
    if (!snap.exists()) return;

    const data = snap.data() || {};
    // Aggiorniamo solo se c'è un ordine recente di riferimento
    if (!data.lastOrderId) return;

    const current = Number(data.inviiCycleCount || 0);
    const next = current + 1;
    const patch = {
      inviiCycleCount: next,
      updatedAt: new Date().toISOString(),
    };
    if (next >= 2) {
      patch.statoUtente = 'Attivo';
    }
    await updateDoc(userRef, patch);
  } catch (err) {
    console.warn('bumpUserCycleAfterDailySend error:', err);
  }
}

export async function saveDailySend({
  userId,
  userData,
  percorsoId = null,
  percorsoTitle = null,
  percorsoSequenceIndex = null,
  percorsoSequenceTotal = null,
  stepKey = null,
  stepIndex = null,
  stepLabel = null,
  stepSequenceIndex = null,
  stepSequenceTotal = null,
  deliveryTargetMode = null,
} = {}) {
  if (!userId) {
    throw new Error('saveDailySend: userId richiesto');
  }

  const db = getFirestore();
  let userSnapshot = userData ? { ...userData } : null;

  if (!userSnapshot) {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      throw new Error('saveDailySend: utente non trovato');
    }
    userSnapshot = { ...(userSnap.data() || {}) };
  }

  const alimentazioniRef = doc(db, 'alimentazioni', userId);
  const invioRef = doc(collection(db, 'inviidigiornata'));
  const now = new Date();
  const deliveryDate = computeNextWorkingDate(now, 3);

  const newAlimentazione = buildNewAlimentazione(now, {
      percorsoId,
      percorsoTitle,
      percorsoSequenceIndex,
      percorsoSequenceTotal,
      invioStepKey: stepKey,
      invioStepIndex: stepIndex,
      invioStepLabel: stepLabel,
      invioStepSequenceIndex: stepSequenceIndex,
      invioStepSequenceTotal: stepSequenceTotal,
      invioDeliveryTargetMode: deliveryTargetMode,
    });

  const payload = {
    uid: userId,
    name: userSnapshot.name || '',
    surname: userSnapshot.surname || '',
    email: userSnapshot.email || '',
    phone: userSnapshot.phone || '',
    notes: userSnapshot.notes || '',
    noteAlimentazione: userSnapshot.noteAlimentazione || '',
    noteAllenamento: userSnapshot.noteAllenamento || '',
    studio: userSnapshot.studio || '',
    statoUtente: userSnapshot.statoUtente || '',
    createdAt: serverTimestamp(),
    dataConsegna: Timestamp.fromDate(deliveryDate),
    rowColor: '',
  };

  // Create the send and its canonical entry together, preserving concurrent assignments.
  const alimentazioni = await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(alimentazioniRef);
    const raw = snapshot.exists() ? snapshot.data()?.alimentazioni : [];
    const entries = normalizeAlimentazioni(Array.isArray(raw) ? raw : []);
    entries.push(newAlimentazione);
    transaction.set(alimentazioniRef, { alimentazioni: entries, userId }, { merge: true });
    transaction.set(invioRef, {
      ...payload,
      alimentazioni: entries,
      ...buildLatestProgramMirror(entries),
    });
    return entries;
  });
  await bumpUserCycleAfterDailySend(userId);

  return {
    dataConsegna: deliveryDate,
    alimentazioni,
  };
}
